/**
 * @file Leads view page.
 * @description Displays paginated leads with filters, refresh overlay, and summary counters.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import FilterBar from '../../components/FilterBar';
import LeadTable from '../../components/LeadTable';
import { Lead, LeadsFilters, ApiService } from '../../services/apiService';
import { PageHeader, Panel, Pagination } from '../../components/ui';

/** Rows per page for the leads table (server pagination). */
const PER_PAGE = 10;

const LeadsView: React.FC = () => {
  const location = useLocation();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [currentFilters, setCurrentFilters] = useState<LeadsFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [pageSize, setPageSize] = useState(PER_PAGE);

  const initialFetchDoneRef = useRef(false);

  const fetchLeadsData = useCallback(async (filters: LeadsFilters, page: number = 1) => {
    const isFirstPaint = !initialFetchDoneRef.current;
    setErrorMsg(null);

    if (isFirstPaint) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const result = await ApiService.getLeads(filters, page, PER_PAGE);

      setLeads(result.leads);
      setTotalLeads(result.pagination.totalRecords);
      setTotalPages(result.pagination.totalPages);
      setCurrentPage(result.pagination.currentPage);
      setPageSize(result.pagination.perPage || PER_PAGE);
    } catch (error) {
      setErrorMsg(error instanceof Error ? error.message : 'Failed to fetch leads');
      setLeads([]);
      setTotalLeads(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
      initialFetchDoneRef.current = true;
    }
  }, []);

  const handleFiltersChange = useCallback(
    async (filters: Record<string, string>) => {
      const leadsFilters: LeadsFilters = {
        advertiser_id: filters['advertiser_id'],
        campaign_id: filters['campaign_id'],
        source: filters['source'],
        date_from: filters['date_from'],
        date_to: filters['date_to'],
        min_age: filters['min_age'],
        max_age: filters['max_age'],
        min_income: filters['min_income'],
        max_income: filters['max_income'],
        data_type: filters['data_type'],
        company_name: filters['company_name'],
        vehicle_brand_name: filters['vehicle_brand_name'],
        dnd_status: filters['dnd_status'],
      };
      setCurrentFilters(leadsFilters);
      await fetchLeadsData(leadsFilters, 1);
    },
    [fetchLeadsData],
  );

  const handlePageChange = useCallback(
    async (newPage: number) => {
      if (
        newPage < 1 ||
        newPage > totalPages ||
        newPage === currentPage ||
        refreshing
      ) {
        return;
      }
      await fetchLeadsData(currentFilters, newPage);
    },
    [fetchLeadsData, currentFilters, totalPages, currentPage, refreshing],
  );

  useEffect(() => {
    void fetchLeadsData(currentFilters, 1);
  }, [location.pathname, location.key]); // refetch when opening View Leads (e.g. after upload)

  const handleRetry = useCallback(() => {
    void fetchLeadsData(currentFilters, currentPage);
  }, [fetchLeadsData, currentFilters, currentPage]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="View Leads"
        subtitle="Browse and filter your lead database"
        // icon={Eye}
        className="shadow-md"
      />

      <FilterBar
        onFiltersChange={handleFiltersChange}
        showDataType
        gridClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2"
      />

      <Panel padded className="min-h-[300px] p-0">
        <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-900">All Leads</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-full pt-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading leads...</p>
            </div>
          </div>
        ) : errorMsg ? (
          <div className="text-center text-red-600 py-10">
            <p className="font-semibold">Failed to load leads</p>
            <p className="text-sm mt-1">{errorMsg}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="relative">
              {refreshing && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-[1px]"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md border border-gray-100">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                    <span className="text-sm font-medium text-gray-700">Updating…</span>
                  </div>
                </div>
              )}

              {leads.length === 0 ? (
                <div className="text-center py-12 text-gray-500 px-4">
                  <p className="text-lg font-medium text-gray-700">No leads found</p>
                  <p className="text-sm mt-2 max-w-md mx-auto">
                    Try adjusting filters — use <strong>Data Type</strong> (Lead / Clicker) to match
                    how you uploaded, then click <strong>Filters</strong>. Reset filters if needed.
                  </p>
                </div>
              ) : (
                <LeadTable
                  embedded
                  leads={leads}
                  serialOffset={(currentPage - 1) * pageSize}
                />
              )}
            </div>

            <Pagination
              className="mt-6"
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalLeads}
              pageSize={pageSize}
              onPageChange={(page) => void handlePageChange(page)}
              itemLabel="leads"
              disabled={refreshing}
              ariaLabel="Leads pagination"
            />
          </>
        )}
      </Panel>
    </div>
  );
};

export default LeadsView;
